import React from 'react';
import { Typography } from 'antd';
import { CarOutlined, ClusterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatMoneyPl } from '../../../utils/currency';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import type { ReceiptTruckChoiceGroup } from '../api/invoiceWorkflowApi';
import './ReceiptTruckChoice.css';

const { Text } = Typography;

/**
 * Owner 2026-10-12 („fracht idzie za autem”): when the goods of this receipt
 * came on one known truck, its PZ batch takes that truck's freight; otherwise
 * the freight of the ZD's trucks is averaged over its batches. One choice per
 * ZD of the invoice; the rule itself lives on the server.
 */
export function defaultTruckChoice(groups: ReceiptTruckChoiceGroup[]): Record<string, string | null> {
  return Object.fromEntries(groups.map((group) => {
    const open = group.trucks.filter((truck) => truck.carriedQuantity === null || truck.carriedQuantity > truck.receivedQuantity + 0.0005);
    return [group.supplierOrderId, open.length === 1 ? open[0].id : null];
  }));
}

/** More than one truck still to receive: one receipt per truck (owner 2026-10-12). */
export function hasSeveralOpenTrucks(groups: ReceiptTruckChoiceGroup[]): boolean {
  return groups.some((group) => group.trucks
    .filter((truck) => truck.carriedQuantity === null || truck.carriedQuantity > truck.receivedQuantity + 0.0005).length > 1);
}

export function chosenTruckIds(value: Record<string, string | null>): string[] {
  return Object.values(value).filter((id): id is string => Boolean(id));
}

interface Props {
  groups: ReceiptTruckChoiceGroup[];
  value: Record<string, string | null>;
  onChange: (value: Record<string, string | null>) => void;
  disabled?: boolean;
}

const ReceiptTruckChoice: React.FC<Props> = ({ groups, value, onChange, disabled = false }) => {
  const { t, i18n } = useTranslation(['invoices']);
  const shown = groups.filter((group) => group.trucks.length > 0);
  if (shown.length === 0) return null;
  const text = (key: string, params?: Record<string, unknown>) => t(`invoices:workflow.truckChoice.${key}`, params);
  const amount = (quantity: number | null, unit: string | null) => (quantity === null
    ? '—'
    : `${formatLocalizedQuantity(quantity, i18n.language)} ${formatUnit(unit, i18n.language)}`);

  return (
    <section className="receipt-truck-choice" data-testid="receipt-truck-choice">
      {shown.map((group) => {
        const selected = value[group.supplierOrderId] ?? null;
        const pick = (truckId: string | null) => onChange({ ...value, [group.supplierOrderId]: truckId });
        return (
          <div key={group.supplierOrderId} className="receipt-truck-choice__group" role="radiogroup" aria-label={text('question', { zd: group.supplierOrderLabel })}>
            <div className="receipt-truck-choice__head">
              <strong>{text('question', { zd: group.supplierOrderLabel })}</strong>
              <Text type="secondary">{text('hint')}</Text>
            </div>
            <div className="receipt-truck-choice__options">
              {[...group.trucks].sort((a, b) => a.label.localeCompare(b.label, 'pl', { numeric: true })).map((truck) => {
                const done = truck.carriedQuantity !== null && truck.receivedQuantity >= truck.carriedQuantity - 0.0005;
                return (
                  <button
                    key={truck.id}
                    type="button"
                    role="radio"
                    aria-checked={selected === truck.id}
                    disabled={disabled}
                    className={`receipt-truck-choice__option${selected === truck.id ? ' is-on' : ''}${done ? ' is-done' : ''}`}
                    data-testid={`receipt-truck-option-${truck.id}`}
                    onClick={() => pick(truck.id)}
                  >
                    <CarOutlined className="receipt-truck-choice__icon" aria-hidden="true" />
                    <span className="receipt-truck-choice__text">
                      <span className="receipt-truck-choice__label" title={truck.label}>{truck.label}</span>
                      <span className="receipt-truck-choice__freight">
                        {truck.freightPLN === null ? text('freightUnknown') : text('freight', { amount: formatMoneyPl(truck.freightPLN, 'PLN') })}
                      </span>
                      <span className="receipt-truck-choice__meta">
                        {text('carried', { amount: amount(truck.carriedQuantity, truck.unit) })}
                        {' · '}
                        {done
                          ? text('allReceived')
                          : text('received', { amount: amount(truck.receivedQuantity, truck.unit) })}
                      </span>
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                role="radio"
                aria-checked={selected === null}
                disabled={disabled}
                className={`receipt-truck-choice__option receipt-truck-choice__option--average${selected === null ? ' is-on' : ''}`}
                data-testid={`receipt-truck-option-average-${group.supplierOrderId}`}
                onClick={() => pick(null)}
              >
                <ClusterOutlined className="receipt-truck-choice__icon" aria-hidden="true" />
                <span className="receipt-truck-choice__text">
                  <span className="receipt-truck-choice__label">{text('average')}</span>
                  <span className="receipt-truck-choice__meta">{text('averageHint')}</span>
                </span>
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
};

export default ReceiptTruckChoice;
